/**
 * bootTrace — earliest-possible fatal-error beacon for release builds.
 *
 * WHY THIS EXISTS (TestFlight boot-crash forensics, 2026-08-05): iOS builds
 * 4.2.x (36) crash-looped 0.2–1s after launch with only a native SIGABRT
 * (`RCTFatal` → `RCTExceptionsManager reportFatal`) in the TestFlight crash
 * log — the underlying JS error message never leaves the device, Sentry shows
 * ZERO events for those builds (the app dies before `Sentry.init`'s native
 * layer can persist anything), and Apple strips the exception reason from
 * TestFlight `.crash` files. This module closes that blind spot.
 *
 * It MUST stay 100% dependency-free (no imports at all — not even
 * react-native): anything it pulled in could itself be the module whose eval
 * crashes boot, and its position as the FIRST import of `index.js` (ES module
 * evaluation order is source order) is what guarantees it runs before every
 * other module factory.
 *
 * What it does: wraps the pre-existing global error handler (Hermes installs
 * `ErrorUtils` before any app code runs) and, on a FATAL error, fire-and-forgets
 * the message + stack to the backend's PUBLIC pre-login crash endpoint
 * (`POST /api/error/log` — anonymous-safe by design, see backend
 * `errorLog.controller.createLog`) before delegating to the original handler
 * (which triggers the native crash). `fetch` is provided by React Native's
 * InitializeCore polyfills, which run before ANY bundle module — even before
 * this one — so it is always available here; if it somehow is not, the guard
 * degrades to a silent no-op and boot behavior is unchanged.
 */

// Hardcoded (NOT imported from ApiConfig) so this module keeps zero deps.
const BOOT_TRACE_URL = 'https://api.happysolutions.hu/api/error/log';

try {
    if (typeof ErrorUtils !== 'undefined' && ErrorUtils.getGlobalHandler && ErrorUtils.setGlobalHandler) {
        const previousHandler = ErrorUtils.getGlobalHandler();

        ErrorUtils.setGlobalHandler((error, isFatal) => {
            try {
                if (isFatal && typeof fetch === 'function') {
                    // Deliberately not awaited: the process is about to abort;
                    // the request only needs to be handed to the network stack.
                    fetch(BOOT_TRACE_URL, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            source: 'bootTrace',
                            fatal: true,
                            message: String((error && error.message) || error),
                            stack: String((error && error.stack) || ''),
                            name: String((error && error.name) || 'Error'),
                        }),
                    }).catch(() => {});
                }
            } catch (_) {
                // Beacon must never interfere with crash handling.
            }

            if (typeof previousHandler === 'function') {
                previousHandler(error, isFatal);
            }
        });
    }
} catch (_) {
    // If wiring the handler fails, boot proceeds exactly as before.
}
